import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getFileViewerTranslations } from '../../constants/file-viewer.translations';
import { clampNumber, downloadFile, filenameFromPath, openFileInNewTab, safeDecodeURIComponent } from '../../utils/file-viewer.utils';

@Component({
  selector: 'app-file-viewer-image',
  imports: [CommonModule],
  templateUrl: './file-viewer-image.component.html',
  styleUrl: './file-viewer-image.component.scss',
})
export class FileViewerImageComponent {
  @ViewChild('viewport', { static: true })
  viewportRef!: ElementRef<HTMLElement>;
  @ViewChild('imageEl') imageRef?: ElementRef<HTMLImageElement>;
  @Input() isComponent: boolean = false;
  @Input() src: string | null = null;
  @Input() name: string | null = null;
  @Input() languageCode = 'it';

  readonly minScale = 0.2;
  readonly maxScale = 8;

  imageUrl: string | null = null;
  imageTitle: string = '';

  scale = 1;
  tx = 0;
  ty = 0;

  private isDragging = false;
  private dragStartClientX = 0;
  private dragStartClientY = 0;
  private dragStartTx = 0;
  private dragStartTy = 0;

  private naturalWidth = 0;
  private naturalHeight = 0;

  angle = 0;

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  get translations() {
    return getFileViewerTranslations(this.languageCode);
  }

  ngOnInit(): void {
    this._resolveImageSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src'] || changes['name']) {
      this._resolveImageSource();
    }
  }

  ngAfterViewInit(): void {
    if (!this.imageUrl || !this.imageRef) {
      return;
    }

    const img = this.imageRef.nativeElement;
    if (img.complete && img.naturalWidth > 0) {
      this._onImageLoaded();
      this.cdr.detectChanges();
      return;
    }

    img.addEventListener('load', () => this._onImageLoaded(), { once: true });
  }

  onPointerDown(ev: PointerEvent): void {
    if (!this.imageUrl) {
      return;
    }

    this.isDragging = true;
    this.viewportRef.nativeElement.setPointerCapture(ev.pointerId);
    this.dragStartClientX = ev.clientX;
    this.dragStartClientY = ev.clientY;
    this.dragStartTx = this.tx;
    this.dragStartTy = this.ty;
  }

  onPointerMove(ev: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }

    const dx = ev.clientX - this.dragStartClientX;
    const dy = ev.clientY - this.dragStartClientY;
    this.tx = this.dragStartTx + dx;
    this.ty = this.dragStartTy + dy;
  }

  onPointerUp(ev: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }

    this.isDragging = false;
    try {
      this.viewportRef.nativeElement.releasePointerCapture(ev.pointerId);
    } catch {
      // ignore
    }
  }

  onWheel(ev: WheelEvent): void {
    // Mimic most viewers: zoom on Ctrl+wheel (trackpad pinch usually maps to Ctrl+wheel)
    if (!ev.ctrlKey) {
      return;
    }

    ev.preventDefault();

    const factor = ev.deltaY < 0 ? 1.12 : 0.88;
    this._zoomAt(factor, ev.clientX, ev.clientY);
  }

  zoomIn(): void {
    this._zoomAt(1.2);
  }

  zoomOut(): void {
    this._zoomAt(1 / 1.2);
  }

  rotate(): void {
    this.angle = (this.angle + 90) % 360;
    this.fitToScreen();
  }

  fitToWidth(): void {
    if (!this._canFit()) {
      return;
    }

    const viewport = this.viewportRef.nativeElement.getBoundingClientRect();
    const { width } = this._getBaseDimensions();
    const scale = clampNumber(
      viewport.width / width,
      this.minScale,
      this.maxScale,
    );
    this._applyFit(scale, 'width');
  }

  fitToScreen(): void {
    if (!this._canFit()) {
      return;
    }

    const viewport = this.viewportRef.nativeElement.getBoundingClientRect();
    const { width, height } = this._getBaseDimensions();
    const scaleX = viewport.width / width;
    const scaleY = viewport.height / height;
    const scale = clampNumber(Math.min(scaleX, scaleY), this.minScale, this.maxScale);
    this._applyFit(scale, 'screen');
  }

  download(): void {
    if (!this.imageUrl) {
      return;
    }

    downloadFile(this.imageUrl, this.imageTitle || this.translations.imageFallbackTitle);
  }

  openInNewTab(): void {
    if (!this.imageUrl) {
      return;
    }

    openFileInNewTab(this.imageUrl);
  }

  get transform(): string {
    const { offsetX, offsetY } = this._getRotationOffset();
    return `translate(${this.tx + offsetX}px, ${this.ty + offsetY}px) rotate(${this.angle}deg) scale(${this.scale})`;
  }

  private _onImageLoaded(): void {
    if (!this.imageRef) {
      return;
    }

    const img = this.imageRef.nativeElement;
    this.naturalWidth = img.naturalWidth;
    this.naturalHeight = img.naturalHeight;
    this._queueFitToScreen();
  }

  private _zoomAt(factor: number, clientX?: number, clientY?: number): void {
    const viewportEl = this.viewportRef.nativeElement;
    const rect = viewportEl.getBoundingClientRect();

    const targetScale = clampNumber(
      this.scale * factor,
      this.minScale,
      this.maxScale,
    );
    if (targetScale === this.scale) {
      return;
    }

    const px = (clientX ?? rect.left + rect.width / 2) - rect.left;
    const py = (clientY ?? rect.top + rect.height / 2) - rect.top;

    // Keep the point under cursor stable.
    const cx = (px - this.tx) / this.scale;
    const cy = (py - this.ty) / this.scale;

    this.scale = targetScale;
    this.tx = px - cx * this.scale;
    this.ty = py - cy * this.scale;
  }

  private _canFit(): boolean {
    return this.naturalWidth > 0 && this.naturalHeight > 0;
  }

  private _applyFit(scale: number, mode: 'width' | 'screen'): void {
    this.scale = scale;
    const viewport = this.viewportRef.nativeElement.getBoundingClientRect();
    const { width: contentW, height: contentH } = this._getRenderedDimensions();

    // Center content for "screen", keep top padding for "width" (PDF-like).
    this.tx = Math.round((viewport.width - contentW) / 2);
    this.ty =
      mode === 'screen'
        ? Math.round((viewport.height - contentH) / 2)
        : Math.round(Math.max(16, (viewport.height - contentH) / 6));
  }

  private _queueFitToScreen(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.fitToScreen();
        this.cdr.detectChanges();
      });
    });
  }

  private _getBaseDimensions(): { width: number; height: number } {
    const normalizedAngle = this._getNormalizedAngle();
    const isVertical = normalizedAngle === 90 || normalizedAngle === 270;

    return {
      width: isVertical ? this.naturalHeight : this.naturalWidth,
      height: isVertical ? this.naturalWidth : this.naturalHeight,
    };
  }

  private _getRenderedDimensions(): { width: number; height: number } {
    const { width, height } = this._getBaseDimensions();
    return {
      width: width * this.scale,
      height: height * this.scale,
    };
  }

  private _getRotationOffset(): { offsetX: number; offsetY: number } {
    const normalizedAngle = this._getNormalizedAngle();
    const scaledWidth = this.naturalWidth * this.scale;
    const scaledHeight = this.naturalHeight * this.scale;

    switch (normalizedAngle) {
      case 90:
        return { offsetX: scaledHeight, offsetY: 0 };
      case 180:
        return { offsetX: scaledWidth, offsetY: scaledHeight };
      case 270:
        return { offsetX: 0, offsetY: scaledWidth };
      default:
        return { offsetX: 0, offsetY: 0 };
    }
  }

  private _getNormalizedAngle(): number {
    return ((this.angle % 360) + 360) % 360;
  }

  private _resolveImageSource(): void {
    const explicitSrc = this.src;
    const explicitName = this.name;
    const rawName = this.route.snapshot.paramMap.get('name');
    const raw = this.route.snapshot.paramMap.get('src');
    const decodedName = rawName ? safeDecodeURIComponent(rawName) : null;
    const decoded = raw ? safeDecodeURIComponent(raw) : null;

    if (explicitSrc) {
      this.imageUrl = explicitSrc;
      this.imageTitle = explicitName || filenameFromPath(explicitSrc);
      return;
    }

    if (decoded) {
      this.imageUrl = decoded;
      this.imageTitle = decodedName || filenameFromPath(decoded);
      return;
    }

    this.imageUrl = null;
    this.imageTitle = '';
  }
}
